import { useState, useEffect } from 'react';
import { Check, AlertCircle, Search } from 'lucide-react';
import axios from '@/lib/axios';
import { Playlist } from '@/types/playlist';
import { Song } from '@/types/song';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';

interface RemoveFromPlaylistModalProps {
  open: boolean;
  onClose: () => void;
  selectedSongs: Song[];
  onSuccess: () => void;
}

export function RemoveFromPlaylistModal({ 
  open, 
  onClose, 
  selectedSongs, 
  onSuccess 
}: RemoveFromPlaylistModalProps) {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [removingInProgress, setRemovingInProgress] = useState<boolean>(false);

  // Playlistleri yükle
  useEffect(() => {
    if (open) {
      fetchPlaylists();
      // Modal açıldığında seçimi temizle
      setSelectedPlaylistId(null);
    }
  }, [open]);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get('/playlists');
      console.log("Yüklenen playlist'ler:", data);
      setPlaylists(data || []);
    } catch (err) {
      console.error('Playlist yüklenirken hata:', err);
      setError('Playlistler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Seçilen playlist objesini döndür
  const getSelectedPlaylist = (): Playlist | null => {
    if (!selectedPlaylistId) return null;
    return playlists.find(p => p._id === selectedPlaylistId) || null;
  };

  // Şarkıları seçilen playlist'ten çıkar
  const handleRemoveFromPlaylist = async () => {
    if (!selectedPlaylistId) {
      setError('Lütfen bir playlist seçin');
      return;
    }

    if (selectedSongs.length === 0) {
      setError('Çıkarılacak şarkı bulunamadı');
      return;
    }

    try {
      setRemovingInProgress(true);
      setError(null);

      const selectedPlaylist = getSelectedPlaylist();
      if (!selectedPlaylist) {
        throw new Error('Seçilen playlist bulunamadı');
      }

      // Şarkı ID'lerini al
      const songIds = selectedSongs.map(song => song._id);

      console.log(`${selectedPlaylist.name} isimli playlist'ten ${songIds.length} şarkı çıkarılıyor`);

      // API isteği gönder
      await axios.delete(`/playlists/${selectedPlaylistId}/songs`, {
        data: {
          songs: songIds,
          updateForAllCompanies: true
        }
      });

      // Çıkarma işleminden sonra playlist'i yeniden çek (detaylar için)
      const { data: updatedPlaylist } = await axios.get(`/playlists/${selectedPlaylistId}`);
      console.log('Güncellenmiş playlist:', updatedPlaylist);

      // Başarılı mesajı göster
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Şarkılar çıkarılırken hata:', err);
      setError(
        err.response?.data?.message || 
        'Şarkılar playlist\'ten çıkarılırken bir hata oluştu. Lütfen tekrar deneyin.'
      );
    } finally {
      setRemovingInProgress(false);
    }
  };

  // Filtreleme işlemi
  const filteredPlaylists = searchValue.trim() 
    ? playlists.filter(playlist => 
        playlist.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : playlists;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Playlist'ten Çıkar</DialogTitle>
          <DialogDescription>
            {selectedSongs.length} şarkıyı çıkarmak için bir playlist seçin
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-2">
          <div className="mb-4">
            <div className="flex items-center border rounded-md px-3">
              <Search className="h-4 w-4 text-muted-foreground mr-2" />
              <Input
                placeholder="Playlist ara..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border-0 focus-visible:ring-0"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
            </div>
          ) : (
            <ScrollArea className="h-[300px] pr-4">
              {filteredPlaylists.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  Playlist bulunamadı
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredPlaylists.map((playlist) => (
                    <div 
                      key={playlist._id} 
                      className={`p-3 border rounded-md hover:bg-gray-50 transition-colors cursor-pointer ${
                        selectedPlaylistId === playlist._id ? 'bg-gray-50 border-primary ring-1 ring-primary' : ''
                      }`}
                      onClick={() => {
                        console.log('Playlist seçildi:', playlist.name, playlist._id);
                        setSelectedPlaylistId(playlist._id);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{playlist.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {playlist.songs?.length || 0} şarkı | {playlist.totalDuration ? Math.floor(playlist.totalDuration / 60) : 0} dakika
                          </p>
                        </div>
                        {selectedPlaylistId === playlist._id && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={removingInProgress}>
            İptal
          </Button>
          <Button 
            type="submit" 
            variant="destructive"
            onClick={handleRemoveFromPlaylist}
            disabled={!selectedPlaylistId || removingInProgress || loading}
          >
            {removingInProgress ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Çıkarılıyor...
              </>
            ) : (
              `Playlist'ten Çıkar (${selectedSongs.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
